import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { logout, updateUserInfo } from '../features/auth/authSlice';
import { Button, GlassCard } from './ui';

const inputClassName =
  'w-full rounded-btn border border-white/10 bg-bg px-4 py-3 text-sm text-body placeholder:text-muted/70 transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://website-alturion-production.up.railway.app' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const ProfileForm = ({ show, onHide, onUpdate, user }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const dispatch = useDispatch();

  const submitHandler = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (password !== confirmPassword) {
      setError(t('profile.form.errors.passwordMismatch'));
      return;
    }

    if (password && !oldPassword) {
      setError(t('profile.form.errors.oldPasswordRequired'));
      return;
    }

    try {
      setLoading(true);

      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      };

      const userData = {
        name,
        email,
      };

      if (password && oldPassword) {
        userData.password = password;
        userData.oldPassword = oldPassword;
      }

      const { data } = await axios.put(
        getApiUrl('/api/users/profile'),
        userData,
        config
      );

      // Mettre à jour Redux et localStorage
      dispatch(updateUserInfo(data));
      
      setSuccess(true);
      setLoading(false);
      
      if (onUpdate) {
        onUpdate(data);
      }
      
      setOldPassword('');
      setPassword('');
      setConfirmPassword('');
      
      if (email !== user.email) {
        setTimeout(() => {
          if (window.confirm(t('profile.form.errors.emailChanged'))) {
            dispatch(logout());
            window.location.href = '/login';
          }
        }, 1500);
      }
    } catch (error) {
      setLoading(false);
      setError(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/80 backdrop-blur-sm p-4">
      <GlassCard hover={false} className="relative w-full max-w-md mx-auto overflow-hidden">
        <form onSubmit={submitHandler} className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[.08]">
            <h2 className="text-lg font-semibold text-body">{t('profile.account.editTitle')}</h2>
            <button
              type="button"
              onClick={onHide}
              className="text-muted hover:text-body transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {error && (
              <div className="mb-4 bg-danger/10 border border-danger/40 text-danger px-4 py-3 rounded-btn">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 bg-success/10 border border-success/40 text-success px-4 py-3 rounded-btn">
                {t('profile.messages.profileUpdated')}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.form.name')}
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder={t('profile.form.placeholders.name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.form.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder={t('profile.form.placeholders.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div className="pt-4 border-t border-white/[.08]">
                <h5 className="text-base font-medium text-primary mb-2">{t('profile.form.changePassword')}</h5>
                <p className="text-xs text-muted mb-4">
                  {t('profile.form.passwordNote')}
                </p>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="oldPassword" className="block text-sm font-medium text-muted mb-1">
                      {t('profile.form.currentPassword')}
                    </label>
                    <input
                      type="password"
                      id="oldPassword"
                      placeholder={t('profile.form.placeholders.currentPassword')}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-muted mb-1">
                      {t('profile.form.newPassword')}
                    </label>
                    <input
                      type="password"
                      id="password"
                      placeholder={t('profile.form.placeholders.newPassword')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-muted mb-1">
                      {t('profile.form.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      placeholder={t('profile.form.placeholders.confirmPassword')}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/[.08] flex flex-col sm:flex-row justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onHide}
              className="w-full sm:w-auto"
            >
              {t('profile.form.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? t('profile.form.updating') : t('profile.form.update')}
            </Button>
          </div>
        </form>
      </GlassCard>
    </div>
  );
};

export default ProfileForm;
